import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';
import { useTTS } from '../../hooks/useTTS';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Paper, Box, IconButton, Button } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// removed unused theme import to avoid lint warnings

const PAGE_ID = 'm6_u1_x2_frequenzumrichter';

const M6_U1_X2_Frequenzumrichter: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const images = [
    { key: 'fu', src: '/assets/drawable/motor_fu.png', label: 'Frequenzumrichter für Drehstrommotoren' }
  ];

  const textBlocks = useMemo(() => ([
    { key: 'intro', heading: 'Frequenzumrichter: Die Drehzahlmeister', body: 'Standard-Drehstrom-Asynchronmotoren haben eine feste Nenndrehzahl, die durch die Netzfrequenz und die Polpaarzahl bestimmt wird. Frequenzumrichter (FU) ermöglichen eine stufenlose und präzise Regelung der Motordrehzahl und des Drehmoments, indem sie Frequenz und Spannung der Motorversorgung variabel gestalten. Dies eröffnet völlig neue Möglichkeiten für Energieeffizienz, Prozessoptimierung und Komfort.' },
    { key: 'technical', heading: 'Technische Merkmale', body: 'Frequenzumrichter bieten stufenlose Drehzahlregelung, Energieeinsparung durch bedarfsgerechten Betrieb, Prozessoptimierung, reduzierten Verschleiß durch sanften Anlauf, präzise Drehmomentregelung, Netzfreundlichkeit (reduzierte Einschaltströme), Bremsfunktionen und umfangreiche Schutzfunktionen. Moderne Geräte ermöglichen eine hochpräzise Regelung mit schneller Dynamik und vielfältigen Parametrierungsmöglichkeiten.' },
    { key: 'features', heading: 'Ansteuerung & Schnittstellen', body: 'Frequenzumrichter können über analoge Sollwerte (0-10V, 4-20mA), Digitaleingänge (Start/Stopp, Festdrehzahlen), Bedienfeld (Potentiometer, Display), Feldbus-Kommunikation (Profibus, Profinet, Modbus, EtherCAT, BACnet) oder Impuls-/Frequenzeingang angesteuert werden. Die flexible Integration in Automatisierungssysteme ist ein zentraler Vorteil moderner FU.' },
    { key: 'advantages', heading: 'Hersteller & Auswahl', body: 'Typische Hersteller sind Siemens (SINAMICS), SEW, Danfoss, ABB, Schneider Electric, Lenze, Rockwell Automation, Yaskawa. Bei der Auswahl sind Motordaten (Leistung, Spannung, Strom), Anwendungsanforderungen (Drehzahlbereich, Dynamik, Genauigkeit), Umgebungsbedingungen (Temperatur, Schutzart), Ansteuerung (analog, digital, Feldbus), Schutzfunktionen (Überstrom, Überhitzung, Kurzschluss) und EMV-Konformität zu beachten.' },
    { key: 'application', heading: 'Anwendung in der Praxis', body: 'Frequenzumrichter finden Einsatz in Förderbändern, Pumpen, Ventilatoren, Wicklern, Extrudern und Prozessanlagen. Die Konfiguration erfolgt nach Motordaten (Nennleistung, Spannung, Strom, Nenndrehzahl) und Anwendungsdaten (Drehzahlbereich, Sollwert-Quelle, Hochlaufzeit). Wichtig ist die Einhaltung von Sicherheitsvorschriften (Not-Aus, STO), EMV-Richtlinien (geschirmte Kabel) und Wartungsintervallen (Lüfterreinigung, Kondensatoren-Lebensdauer).' }
  ]), []);

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(() => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })), [override, textBlocks]);
  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };
  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return;
    try { await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token); alert('Gespeichert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return;
    try { await uploadUnitAsset(PAGE_ID, imageKey, file, token); alert('Bild aktualisiert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    color: 'primary',
    sx: { ml: 1 }
  }, React.createElement(VolumeUpIcon));

  return React.createElement(UnitPageShell, {
    title: '6.1.2 Frequenzumrichter',
    actions: ttsBtn
  }, React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          label: img.label,
          onReplace: canUploadImages ? (file) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
        mergedBlocks.map(block => React.createElement(EditableTextBlock, {
          key: block.key,
          pageId: PAGE_ID,
          blockId: block.key,
          heading: block.heading,
          body: block.body,
          onSave: canEditTexts ? (body: string) => handleTextSave(block.key, body) : undefined
        }))
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u1_x99_quiz') }, 'Zum Quiz')
    )
  ));
};

export default M6_U1_X2_Frequenzumrichter;


